import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import FeaturedCabinSection from './components/FeaturedCabinSection';
import CabinTypesSection from './components/CabinTypesSection';
import WhyHighlandsSection from './components/WhyHighlandsSection';
import NearbyCabinsSection from './components/NearbyCabinsSection';
import FAQSection from './components/FAQSection';
import CabinSearchTypesSection from './components/CabinSearchTypesSection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Cabins in Highlands NC', item: `${siteUrl}/cabins-in-highlands-nc` },
      ],
    },
    {
      '@type': 'WebPage',
      name: 'Cabins in Highlands NC',
      description: 'A guide to the best cabins in Highlands, North Carolina — including luxury stays, pet-friendly cabins, and properties near Glen Falls waterfall.',
      url: `${siteUrl}/cabins-in-highlands-nc`,
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What are the best cabins in Highlands, NC?',
          acceptedAnswer: { '@type': 'Answer', text: 'Glen Falls Luxury Cabin is consistently rated among the best cabins in Highlands, NC, with a 5.0-star Airbnb Guest Favorite rating. Other top options include Lark Lodge, Stag Ridge, and Acorn Cabin.' },
        },
        {
          '@type': 'Question',
          name: 'Are there luxury cabins in Highlands, NC?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes, Highlands has a well-developed luxury cabin market. Properties like Glen Falls Luxury Cabin offer premium finishes, stone fireplaces, covered decks, and private wooded settings within minutes of downtown Highlands.' },
        },
        {
          '@type': 'Question',
          name: 'Are there pet-friendly cabins in Highlands, NC?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes, several cabins in Highlands welcome pets. Glen Falls Luxury Cabin is one of the most popular pet-friendly options, offering a wooded setting and easy access to nearby trails.' },
        },
      ],
    },
  ],
};

export default function CabinsInHighlands() {
  return (
    <>
      <title>Cabins in Highlands NC | Best Luxury Cabin Rentals — Highlands, North Carolina</title>
      <meta name="description" content="Find the best cabins in Highlands, NC — luxury mountain retreats, pet-friendly stays, and cabins near Glen Falls waterfall. Curated guide to top-rated cabin rentals in Highlands, North Carolina." />
      <meta name="keywords" content="cabins in Highlands NC, Highlands NC cabin rentals, luxury cabins Highlands North Carolina, pet friendly cabins Highlands NC" />
      <meta property="og:title" content="Cabins in Highlands NC | Best Luxury Cabin Rentals" />
      <meta property="og:description" content="Discover the best cabins in Highlands, NC — from luxury retreats near Glen Falls to pet-friendly mountain escapes." />
      <meta name="geo.region" content="US-NC" />
      <meta name="geo.placename" content="Highlands, North Carolina" />
      <meta name="geo.position" content="35.0499;-83.1985" />
      <meta name="last-modified" content="2026-04-10" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${siteUrl}/cabins-in-highlands-nc`} />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>

      <Navbar />
      <main>
        <HeroSection />

        {/* Editorial Intro */}
        <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-cormorant text-3xl md:text-4xl text-[#1A1A18] font-light leading-relaxed italic mb-8">
              &ldquo;A cabin in Highlands isn&apos;t just a place to sleep — it&apos;s the reason you came.&rdquo;
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto mb-5">
              Highlands, North Carolina sits at over 4,000 feet in the Blue Ridge Mountains — and the cabins here reflect that elevation in every sense. Private, refined, and surrounded by some of the most beautiful forest in the Southeast, a cabin stay in Highlands is unlike anything else in the region.
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto">
              This guide covers the best cabins in Highlands, NC — from luxury retreats near Glen Falls waterfall to pet-friendly escapes close to downtown. For a focused look at waterfall-adjacent stays, see our guide to{' '}
              <Link to="/cabins-near-glen-falls-highlands-nc" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer">
                cabins near Glen Falls
              </Link>.
            </p>
          </div>
        </section>

        <FeaturedCabinSection />
        <CabinTypesSection />
        <WhyHighlandsSection />
        <NearbyCabinsSection />
        <CabinSearchTypesSection />

        {/* Why Stay in a Cabin in Highlands NC */}
        <section className="bg-[#FAFAF8] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#EDE8E0]">
                <i className="ri-landscape-line text-[#2D4A3E] text-sm" />
              </div>
              <h2 className="font-cormorant text-4xl md:text-5xl text-[#1A1A18] font-light">
                Why Stay in a Cabin in Highlands, NC?
              </h2>
            </div>
            <div className="w-12 h-px bg-[#C9A96E] mb-8" />
            <p className="text-[#5C5850] font-jost text-base leading-relaxed mb-5">
              Highlands, North Carolina sits at an elevation of 4,118 feet — one of the highest incorporated towns in the eastern United States — which means summers here are genuinely cool, often 10 to 15 degrees cooler than the surrounding lowlands. That alone draws thousands of visitors every year looking to escape the heat, but the elevation is just the beginning of what makes Highlands so compelling as a cabin destination.
            </p>
            <p className="text-[#5C5850] font-jost text-base leading-relaxed mb-5">
              The area is surrounded by some of the most accessible and beautiful waterfalls in the Blue Ridge Mountains. Glen Falls, Dry Falls, and Bridal Veil Falls are all within a short drive of most Highlands cabins — and staying in a cabin here means you can visit them at your own pace, early in the morning before the crowds, or as many times as you like throughout your stay. For guests who want to be especially close to the falls,{' '}
              <Link to="/cabins-near-glen-falls-highlands-nc" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors">
                cabins near Glen Falls waterfall
              </Link>{' '}
              offer some of the most immersive options in the area.
            </p>
            <p className="text-[#5C5850] font-jost text-base leading-relaxed">
              Beyond the waterfalls, Highlands has a genuinely charming downtown — filled with acclaimed restaurants, art galleries, antique shops, and a Saturday Farmers Market — all within a few minutes&apos; drive of most cabin rentals. Hiking trails like Whiteside Mountain and Panthertown Valley are also nearby. Whether you&apos;re here for a week of outdoor adventure or a long weekend of mountain quiet, a cabin in Highlands, NC gives you the perfect base for all of it.
            </p>
          </div>
        </section>

        <FAQSection />

        {/* CTA */}
        <section className="bg-[#2D4A3E] py-20 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-5">/ Plan Your Stay</p>
            <h2 className="font-cormorant text-5xl md:text-6xl text-white font-light mb-5">
              Book Your Cabin<br /><em className="italic">in Highlands, NC</em>
            </h2>
            <p className="text-white/65 font-jost text-sm leading-relaxed mb-10 max-w-lg mx-auto">
              Glen Falls Luxury Cabin is our top recommendation for cabins in Highlands, NC. Check availability and book directly or through Airbnb.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://glenfallscabin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#C9A96E] hover:bg-[#B8954F] text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                View Cabin Details
              </a>
              <Link
                to="/things-to-do"
                className="border border-white/40 text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                Things to Do in Highlands
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
